import { type Room, RoomEvent } from "matrix-js-sdk/src/matrix";
import { useEffect, useState } from "react";

import { type IOOBData } from "../stores/ThreepidInviteStore";
import { useTypedEventEmitter } from "./useEventEmitter";
import { _t } from "../languageHandler";
import { useDmMember } from "../components/views/avatars/WithPresenceIndicator";
import { useContactFormatWatcher } from "./useContactFormatWatcher";
import { getRoomDisplayName } from "../utils/event/eventUtils";

const getRoomName = (room?: Room, oobName = ""): string => room?.name || oobName;

/**
 * Determines the room name from a combination of the room model and potential
 * out-of-band information
 * @param room - The room model
 * @param oobData - out-of-band information about the room
 * @returns {string} the room name
 */
export function useRoomName(room?: Room, oobData?: IOOBData): string {
    let oobName = _t("common.unnamed_room");
    if (oobData && oobData.name) {
        oobName = oobData.name;
    }

    const [name, setName] = useState<string>(getRoomName(room, oobName));

    useTypedEventEmitter(room, RoomEvent.Name, () => {
        setName(getRoomName(room, oobName));
    });

    useEffect(() => {
        setName(getRoomName(room, oobName));
    }, [room, oobName]);

    return name;
}

/**
 * Ritorna il nome visualizzato della room, gestendo sia chat dirette (DM) che di gruppo:
 * - Se è una chat diretta, ritorna il nome del membro usando getRoomMemberNameWithContactFormat,
 *   gestendo anche la disambiguazione e il formato del contatto.
 * - Altrimenti, ritorna il nome della room (room.name).
 * Reagisce ai cambiamenti del nome della room, del membro DM e del formato del contatto.
 * @param room - La room di cui ottenere il nome.
 * @param oobData - Informazioni out-of-band sulla room (usato come fallback se la room non ha nome).
 * @returns Il nome visualizzato della room.
 */
export function useRoomDisplayName(room?: Room, oobData?: IOOBData): string {
    const oobName = oobData?.name || _t("common.unnamed_room");

    const dmMember = useDmMember(room);
    const { contactFormat } = useContactFormatWatcher();

    const [name, setName] = useState<string>(() => (!room ? oobName : getRoomDisplayName(room, contactFormat)));

    // Reagisce ai cambiamenti del nome della room (per le chat di gruppo)
    useTypedEventEmitter(room, RoomEvent.Name, () =>
        setName(!room ? oobName : getRoomDisplayName(room, contactFormat)),
    );

    // Reagisce ai cambiamenti del membro DM, del formato del contatto e della room
    useEffect(() => {
        setName(!room ? oobName : getRoomDisplayName(room, contactFormat));
    }, [room, oobName, dmMember, contactFormat]);

    return name;
}
